import { api, TCustomAxiosResponse } from '..';
import { apiPaths } from '@/services/api/paths';
import type { components } from '@/types/generated/openapi';

export type TContentCreatorApplicationIneligibilityReason = 'ROLE_NOT_ELIGIBLE' | 'ALREADY_CONTENT_CREATOR';

export type TContentCreatorApplicationMe = components['schemas']['ContentCreatorMeResponseDto'];

export type TContentCreatorApplicationSummary = components['schemas']['ContentCreatorApplicationSummaryDto'];
export type TPublicContentCreatorApplicationMe = components['schemas']['ContentCreatorMeResponseDto'];

export type TStartContentCreatorApplicationBody = {
  email: string;
  firstName: string;
  lastName: string;
};

export type TRecoverContentCreatorApplicationBody = {
  email: string;
};

export type TVerifyContentCreatorApplicationBody = {
  email: string;
  token: string;
};

export type TSubmitContentCreatorApplicationBody = {
  creatorName?: string;
  keepNameSameAsRealName?: boolean;
  keepPhotoSameAsMemberPhoto?: boolean;
  bio: string;
  location: {
    city?: string;
    state?: string;
    zip?: string;
  };
  certifications: string[];
  contentFocus: string[];
  socialLinks: Array<{
    platform: string;
    url: string;
  }>;
  contentPolicy: { documentId: string; version: string };
};

export type TPublicSubmitContentCreatorApplicationParams = {
  publicCredentials: TVerifyContentCreatorApplicationBody;
  body: TSubmitContentCreatorApplicationBody;
};

export type TPublicContentCreatorApplicationPhotoParams = {
  file: File;
  publicCredentials: TVerifyContentCreatorApplicationBody;
};

const createPhotoFormData = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  // formData.append('file', {
  //   uri: IS_ANDROID ? `file://${file.path}` : file.path,
  //   name: file.name ?? file.path.split('/').pop(),
  //   type: file.mime,
  // });

  return formData;
};

export const startContentCreatorApplicationPost = async (
  body: TStartContentCreatorApplicationBody,
  turnstileToken: string,
): Promise<unknown> => {
  const { data } = await api.post<TCustomAxiosResponse<unknown>>(
    apiPaths.contentCreatorApplications.public.start,
    body,
    { headers: { 'cf-turnstile-response': turnstileToken } },
  );

  return data.data;
};

export const recoverContentCreatorApplicationPost = async (
  body: TRecoverContentCreatorApplicationBody,
): Promise<{ success: boolean }> => {
  const { data } = await api.post<TCustomAxiosResponse<{ success: boolean }>>(
    apiPaths.contentCreatorApplications.public.recover,
    body,
  );

  return data.data;
};

export const contentCreatorApplicationMeGet = async (): Promise<TContentCreatorApplicationMe> => {
  const { data } = await api.get<TCustomAxiosResponse<TContentCreatorApplicationMe>>(
    apiPaths.contentCreatorApplications.me,
  );

  return data.data;
};

export const submitContentCreatorApplicationPost = async (
  body: TSubmitContentCreatorApplicationBody,
): Promise<unknown> => {
  const { data } = await api.post<TCustomAxiosResponse<unknown>>(apiPaths.contentCreatorApplications.submit, body);

  return data.data;
};

export const contentCreatorApplicationPhotoPost = async (file: File): Promise<unknown> => {
  const { data } = await api.post<TCustomAxiosResponse<unknown>>(
    apiPaths.contentCreatorApplications.photo,
    createPhotoFormData(file),
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  return data.data;
};

export const publicContentCreatorApplicationPhotoPost = async ({
  file,
  publicCredentials,
}: TPublicContentCreatorApplicationPhotoParams): Promise<unknown> => {
  const { data } = await api.post<TCustomAxiosResponse<unknown>>(
    apiPaths.contentCreatorApplications.public.photo,
    createPhotoFormData(file),
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-content-creator-email': publicCredentials.email,
        'x-content-creator-token': publicCredentials.token,
      },
    },
  );

  return data.data;
};

export const publicSubmitContentCreatorApplicationPost = async ({
  publicCredentials,
  body,
}: TPublicSubmitContentCreatorApplicationParams): Promise<unknown> => {
  const { data } = await api.post<TCustomAxiosResponse<unknown>>(
    apiPaths.contentCreatorApplications.public.submit,
    body,
    {
      headers: {
        'x-content-creator-email': publicCredentials.email,
        'x-content-creator-token': publicCredentials.token,
      },
    },
  );

  return data.data;
};

export const publicContentCreatorApplicationMeGet = async ({
  email,
  token,
}: TVerifyContentCreatorApplicationBody): Promise<TPublicContentCreatorApplicationMe> => {
  const { data } = await api.get<TCustomAxiosResponse<TPublicContentCreatorApplicationMe>>(
    apiPaths.contentCreatorApplications.public.me,
    {
      headers: {
        'x-content-creator-email': email,
        'x-content-creator-token': token,
      },
    },
  );

  return data.data;
};

export const verifyContentCreatorApplicationPost = async (
  body: TVerifyContentCreatorApplicationBody,
): Promise<{ success: boolean }> => {
  const { data } = await api.post<TCustomAxiosResponse<{ success: boolean }>>(
    apiPaths.contentCreatorApplications.public.verify,
    body,
  );

  return data.data;
};

export const checkContentCreatorSocialLink = async (
  body: components['schemas']['CheckContentCreatorSocialLinkDto'],
  publicCredentials?: TVerifyContentCreatorApplicationBody,
): Promise<components['schemas']['ContentCreatorSocialLinkExistsDto']> => {
  const { data } = await api.post<TCustomAxiosResponse<components['schemas']['ContentCreatorSocialLinkExistsDto']>>(
    publicCredentials
      ? apiPaths.contentCreatorApplications.public.checkSocialLink
      : apiPaths.contentCreatorApplications.checkSocialLink,
    body,
    publicCredentials
      ? {
          headers: {
            'x-content-creator-email': publicCredentials.email,
            'x-content-creator-token': publicCredentials.token,
          },
        }
      : undefined,
  );
  if (!data.success || typeof data.data?.exists !== 'boolean') {
    throw new Error('Could not verify social handle availability.');
  }
  return data.data;
};
